import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { animated, useSpring, type SpringValue } from "@react-spring/web";
import { cn } from "@design-lab/shared/lib/tailwindcss";

export const textVariants = cva("", {
  variants: {
    variant: {
      default: "text-foreground",
      muted: "text-muted-foreground",
      primary: "text-primary",
      secondary: "text-secondary-foreground",
      destructive: "text-destructive",
      accent: "text-accent-foreground"
    },
    size: {
      xs: "text-xs",
      sm: "text-sm",
      base: "text-base",
      lg: "text-lg",
      xl: "text-xl",
      "2xl": "text-2xl",
      "3xl": "text-3xl",
      "4xl": "text-4xl"
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold"
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
      justify: "text-justify"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "base",
    weight: "normal",
    align: "left"
  }
});

type AnimatedStyle = {
  [key: string]: SpringValue<any> | string | number;
};

export interface TextProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof textVariants> {
  asChild?: boolean;
  as?: React.ElementType;
  animated?: boolean;
  animatedStyle?: AnimatedStyle;
}

export function Text({
  className,
  variant,
  size,
  weight,
  align,
  asChild = false,
  as: Component = "span",
  animated: isAnimated = false,
  animatedStyle,
  ...props
}: TextProps) {
  const baseClassName = cn(textVariants({ variant, size, weight, align, className }));

  const defaultAnimatedStyle = useSpring({
    from: { transform: "translateX(-100%)" },
    to: { transform: "translateX(300%)" },
    config: { duration: 2500 },
    loop: true
  });

  if (asChild) {
    return <Slot data-slot="text" className={baseClassName} {...props} />;
  }

  if (isAnimated) {
    return (
      <Component
        data-slot="text"
        className={cn(baseClassName, "relative overflow-hidden")}
        {...props}
      >
        <animated.div
          style={animatedStyle || defaultAnimatedStyle}
          className="via-background/80 absolute inset-0 bg-gradient-to-r from-transparent to-transparent"
        />
        {props.children}
      </Component>
    );
  }

  return <Component data-slot="text" className={baseClassName} {...props} />;
}
